/**
 * Проект — один рилс (прежний `project.html.j2`): слева библиотека проекта,
 * справа общий референс, раскадровка (порядок — перетаскиванием за номер,
 * текст — правкой на месте, фото — «+»/drag&drop/Ctrl-V, генерация на каждом
 * кадре) и комментарии. Крупный просмотр варианта — панелью справа.
 *
 * Видео-проект (`video != null`, ролик из платья) — те же кадры плюс вкладки
 * `?tab=shots|scenario|video|cut`, подзаголовок «платье · библиотека · цель · баланс»
 * и панель «Платье» слева; «Общего референса» у него нет (стиль — из библиотеки
 * ролика). Везде, кроме первого шага, левая колонка свёрнута в полоску 64 px с
 * обложкой платья (клик — развернуть): таблицы моделей и сценария помещаются на
 * 1280 px без горизонтальной прокрутки (v2 §1.6). Обычный проект — как раньше.
 *
 * Колонки — `minmax(0,1fr)`, а не неявные `auto`: у неявной колонки грид-элемент
 * не уже своего min-content, и широкая таблица распирала панель за край экрана.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronsLeft, ChevronsRight, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { apiFetch } from "@/lib/api";
import type { ProjectView } from "@/lib/project";
import { errorText, postJson, qk } from "@/lib/project";
import { mediaUrl } from "@/lib/media";
import { projectPollMs } from "@/lib/projectPoll";
import { useProjectVideo, videoKeys, type TimelineShot, type VideoDress, type VideoView } from "@/lib/video";
import { Comments } from "@/components/project/Comments";
import { GenPreview, type PreviewTarget } from "@/components/project/GenPreview";
import { ProjectLibrary } from "@/components/project/ProjectLibrary";
import { ProjectReferences } from "@/components/project/ProjectReferences";
import { ShotCard } from "@/components/project/ShotCard";
import { Btn, Crumbs, ErrorLine, FactsLine, Hint, inputClass, PageState, Panel } from "@/components/project/ui";
import { AnglesPanel } from "@/components/video/AnglesPanel";
import { Sec } from "@/components/video/bits";
import { DressPanel } from "@/components/video/DressPanel";
import { ScenarioPanel } from "@/components/video/ScenarioPanel";
import { Timeline } from "@/components/video/Timeline";
import { VideoGuide } from "@/components/video/VideoGuide";
import { ClipsTab, CutTab } from "@/components/video/VideoTabs";
import { tabOf, tabsFor, type Tab, type TabItem } from "@/lib/projectTabs";


export function ProjectPage() {
  const pid = Number(useParams().projectId);
  const client = useQueryClient();
  const query = useQuery({
    queryKey: qk.project(pid),
    queryFn: () => apiFetch<ProjectView>(`/api/projects/${pid}`),
    enabled: Number.isFinite(pid),
    // Пока у кадров есть незавершённые генерации — карточка перечитывается сама (`projectPollMs`):
    // статусы ракурсов и стиллы хронологии рисуются ИЗ НЕЁ, а опрос вкладки «Видео» считает только
    // клипы и монтажи — пакет ракурсов шёл мимо него (задача Оли №301).
    refetchInterval: (q) => projectPollMs(q.state.data?.pending),
  });
  const data = query.data;
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const [search, setSearch] = useSearchParams();
  /** Левая колонка на «Сценарии»/«Видео» развёрнута руками. */
  const [sideOpen, setSideOpen] = useState(false);

  if (!data) return <PageState loading={query.isPending} error={query.error ? errorText(query.error) : null} />;

  const { project, video } = data;
  const reload = () => client.invalidateQueries({ queryKey: qk.project(pid) });
  // У проекта без видео вкладок нет: `Tabs` не рендерится, шаг всегда «Кадры» (`tabOf` вернёт его).
  const allowedTabs: TabItem[] = video ? tabsFor(video.origin) : [];
  const tab = video ? tabOf(search.get("tab"), allowedTabs) : "shots";
  // У проекта «из кадра» левой колонки НЕТ (задача Оли №329: «перекомпануй, не оставляй такие дыры
  // пустого пространства»). Панель платья низкая, а колонка под неё отводилась во всю высоту —
  // под ней зияла пустота в полэкрана. Платье теперь полосой НАД «Ракурсами» (№321 просила
  // информацию о платье), «Библиотека» с импортом из CRM — инструмент проекта из товара.
  const frame = video?.origin === "frame";
  // У проекта «из кадра» библиотеку прячем, только пока она ПУСТА: загрузка нескольких файлов в кадр
  // кладёт лишние в библиотеку без слота, и другого экрана для них нет (найдено ревью 16.10).
  const lib = data?.library;
  const libraryHasFiles = !!lib && lib.assigned.length + lib.loose.length + lib.broken.length > 0;
  const collapsed = video !== null && !frame && tab !== "shots" && !sideOpen;
  const pickTab = (next: Tab) =>
    setSearch(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "shots") params.delete("tab");
        else params.set("tab", next);
        return params;
      },
      { replace: true },
    );

  return (
    <div className={preview ? "p-5 xl:pr-[460px]" : "p-5"}>
      <Crumbs items={[{ label: "проекты", to: "/projects" }, { label: project.project_name }]} />
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-fg" data-annot={`проект ${project.id}`}>
          {project.project_name}
        </h1>
        {video ? <VideoGuide /> : null}
      </div>
      <p className="mt-1 mb-4 text-[12.5px] text-fg-muted">
        {video ? (
          <VideoSubtitle pid={pid} video={video} />
        ) : (
          <>
            товар <span className="tnum">{project.mp_product_id ?? "—"}</span>
          </>
        )}
        {data.facts.length ? (
          <>
            {" · "}
            <FactsLine facts={data.facts} />
          </>
        ) : null}
        {data.pending > 0 ? (
          <span className="ml-2 rounded-full bg-warn-bg px-2 py-0.5 text-[11.5px] text-warn">
            в очереди и в работе: {data.pending}
          </span>
        ) : null}
      </p>

      {video ? <Tabs value={tab} onPick={pickTab} tabs={allowedTabs} /> : null}

      <div
        className={
          "grid grid-cols-[minmax(0,1fr)] items-start gap-4 " +
          (frame
            ? ""
            : collapsed
              ? "lg:grid-cols-[64px_minmax(0,1fr)]"
              : "lg:grid-cols-[minmax(260px,340px)_minmax(0,1fr)]")
        }
      >
        {frame ? null : video && collapsed ? (
          <DressStrip dress={video.dress} onOpen={() => setSideOpen(true)} />
        ) : video ? (
          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-4">
            {tab !== "shots" ? (
              <button
                type="button"
                onClick={() => setSideOpen(false)}
                className="inline-flex items-center gap-1 justify-self-start text-[12px] text-fg-muted hover:text-fg"
              >
                <ChevronsLeft size={13} />
                свернуть колонку
              </button>
            ) : null}
            <ProjectLibrary pid={pid} library={data.library} slots={data.slots} />
            {/* У проекта «из кадра» отдельной панели исходника нет (задача Оли №314: «исходный кадр
                не должен быть отдельно, он должен быть в ракурсах») — он первая строка «Ракурсов»,
                там же превью и ссылка на его карточку результата. */}
            <DressPanel pid={pid} dress={video.dress} />
          </div>
        ) : (
          <ProjectLibrary pid={pid} library={data.library} slots={data.slots} />
        )}

        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-4">
          {/* Платье проекта «из кадра» — полосой сверху, во всю ширину: панель сбоку оставляла дыру (№329). */}
          {frame && video ? <DressBar dress={video.dress} /> : null}
          {/* Файлы в библиотеке проекта «из кадра» бывают от загрузки нескольких картинок в кадр —
              другого экрана для них нет, поэтому панель показываем, но в общей колонке (№321). */}
          {frame && libraryHasFiles ? <ProjectLibrary pid={pid} library={data.library} slots={data.slots} /> : null}
          {tab === "shots" ? (
            <>
              {video ? (
                <VideoTimeline pid={pid} video={video} shots={data.shots} seedAt={query.dataUpdatedAt} />
              ) : (
                <ProjectReferences
                  pid={pid}
                  note={project.project_note}
                  references={data.references}
                  libraries={data.libraries}
                />
              )}
              <Storyboard
                pid={pid}
                data={data}
                seedAt={query.dataUpdatedAt}
                preview={preview}
                onPreview={setPreview}
                video={video}
              />
            </>
          ) : null}
          {video && tab === "scenario" ? <ScenarioPanel key={pid} pid={pid} video={video} /> : null}
          {video && tab === "video" ? <ClipsTab pid={pid} video={video} /> : null}
          {video && tab === "cut" ? <CutTab pid={pid} video={video} /> : null}
          <Comments
            title="Комментарии к проекту"
            comments={data.comments}
            target={{ project_id: pid }}
            placeholder="замечание менеджера"
            onAdded={reload}
          />
        </div>
      </div>

      {preview ? <GenPreview pid={pid} target={preview} onChange={setPreview} /> : null}
    </div>
  );
}

/**
 * «платье N · библиотека «Milla Nova» · цель 15 с · баланс OpenRouter $6.77».
 *
 * Цель — только у проекта «из платья»: там её задал сотрудник и по ней пишет сценарий композер. У
 * проекта «из кадра» она производная от длительности куска, никто её не вводил, и показывать её как
 * свойство ролика незачем (задача Оли №310).
 */
function VideoSubtitle({ pid, video }: { pid: number; video: VideoView }) {
  // Баланс — из опроса ролика: он же дальше опрашивает клипы, второго запроса шапке не нужно.
  const poll = useProjectVideo(pid);
  const balance = poll.data?.balance;
  const { dress, library } = video;
  return (
    <>
      {dress.id !== null && !dress.deleted ? (
        <Link to={`/photos/${dress.id}`} className="hover:text-accent-text">
          {dress.name ?? `платье ${dress.id}`}
        </Link>
      ) : (
        <span className="text-fg-faint">{dress.id !== null && dress.deleted ? "платье удалено" : "без платья"}</span>
      )}
      {" · "}
      {library ? (
        <>
          библиотека{" "}
          <Link to={`/lib/${library.id}`} className="hover:text-accent-text">
            «{library.name}»
          </Link>
        </>
      ) : (
        <span className="text-warn">без стилевой библиотеки</span>
      )}
      {video.origin === "frame" ? null : (
        <>
          {" · "}цель <Sec ms={video.duration_ms} />
        </>
      )}
      {video.video_ref ? (
        <>
          {" · "}по референсу{" "}
          <Link to={`/video-refs/${video.video_ref.id}`} className="hover:text-accent-text" data-annot="проект: референс">
            {video.video_ref.id} ↗
          </Link>
        </>
      ) : null}
      {balance ? (
        <>
          {" · "}баланс OpenRouter <span className="tnum">${balance.left.toFixed(2)}</span>
        </>
      ) : null}
    </>
  );
}

/**
 * Платье проекта «из кадра» одной полосой над «Ракурсами» (задача Оли №329): обложка, название
 * ссылкой, счётчик фото и несколько миниатюр. Панель сбоку (№321) давала ту самую дыру — низкий
 * блок в колонке высотой в полэкрана. Слотов и «Наполнить заново» тут нет: это инструменты проекта
 * из товара.
 */
function DressBar({ dress }: { dress: VideoDress }) {
  const cover = dress.photos[0];
  const src = cover ? (cover.thumb_path ?? cover.view_path) : null;
  const text =
    dress.id === null
      ? "ролик из картинки без папки платья"
      : dress.deleted
        ? "папку платья удалили — готовые кадры и клипы остаются"
        : null;
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border border-border bg-bg-subtle px-3 py-2"
      data-annot="платье ролика"
    >
      <span className="text-[12px] text-fg-faint">Платье</span>
      {text ? (
        <span className="text-[12.5px] text-fg-muted">{text}</span>
      ) : (
        <>
          <Link to={`/photos/${dress.id}`} className="flex-none">
            {src ? (
              <img src={mediaUrl(src)} alt="" className="h-[60px] w-[40px] rounded-[3px] border border-border object-cover" />
            ) : (
              <span className="grid h-[60px] w-[40px] place-items-center rounded-[3px] border border-dashed border-border-strong text-[10px] text-fg-faint">
                нет фото
              </span>
            )}
          </Link>
          <Link to={`/photos/${dress.id}`} className="text-[13px] font-medium text-fg hover:text-accent-text">
            {dress.name}
          </Link>
          <span className="text-[12px] text-fg-faint">фото: {dress.photos.length}</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {dress.photos.slice(1, 5).map((p) =>
              p.thumb_path || p.view_path ? (
                <img
                  key={p.id}
                  src={mediaUrl(p.thumb_path ?? p.view_path)}
                  alt=""
                  loading="lazy"
                  className="h-[40px] w-[27px] rounded-[3px] border border-border object-cover"
                />
              ) : null,
            )}
          </div>
          <Link to={`/photos/${dress.id}`} className="ml-auto text-[12px] text-fg-muted hover:text-accent-text">
            в «Наших фото» →
          </Link>
        </>
      )}
    </div>
  );
}

/** Свёрнутая левая колонка (64 px): обложка платья; клик — развернуть колонку. У проекта «из кадра»
 *  в ней только «Платье» (библиотеки у него нет, №321), поэтому подпись о составе не говорит. */
function DressStrip({ dress, onOpen }: { dress: VideoDress; onOpen: () => void }) {
  const cover = dress.photos[0];
  const src = cover ? (cover.thumb_path ?? cover.view_path) : null;
  return (
    <button
      type="button"
      onClick={onOpen}
      title="развернуть колонку с платьем"
      aria-label="развернуть колонку с платьем"
      className="grid w-16 justify-items-center gap-1.5 rounded-[var(--radius-card)] border border-border bg-bg-subtle px-1.5 py-2 text-fg-muted hover:border-border-strong hover:text-fg"
    >
      {src ? (
        <img src={mediaUrl(src)} alt="" className="h-[80px] w-[48px] rounded-[3px] border border-border object-cover" />
      ) : (
        <span className="grid h-[80px] w-[48px] place-items-center rounded-[3px] border border-dashed border-border-strong text-[10px] text-fg-faint">
          нет фото
        </span>
      )}
      <span className="text-[10.5px] leading-tight">платье</span>
      <ChevronsRight size={14} />
    </button>
  );
}

/**
 * Хронология ролика свежее из двух источников: карточки проекта (её перечитывает любая правка кадра)
 * и опроса клипов (он идёт, пока клипы в работе). У обычного проекта опроса нет — пусто.
 */
const NO_TIMELINE: TimelineShot[] = [];

function useTimeline(pid: number, video: VideoView | null, seedAt: number): TimelineShot[] {
  const poll = useProjectVideo(pid, video !== null);
  if (!video) return NO_TIMELINE;
  return poll.data && poll.dataUpdatedAt > seedAt ? poll.data.timeline : video.timeline;
}

function VideoTimeline({
  pid,
  video,
  shots,
  seedAt,
}: {
  pid: number;
  video: VideoView;
  shots: ProjectView["shots"];
  seedAt: number;
}) {
  const timeline = useTimeline(pid, video, seedAt);
  return (
    <>
      {video.origin === "frame" ? <AnglesPanel pid={pid} shots={shots} timeline={timeline} /> : null}
      <Timeline
        pid={pid}
        shots={shots}
        timeline={timeline}
        allowStillPicker={video.origin === "frame"}
      />
    </>
  );
}

function Tabs({ value, onPick, tabs }: { value: Tab; onPick: (tab: Tab) => void; tabs: TabItem[] }) {
  return (
    <div role="tablist" aria-label="Разделы ролика" className="mb-4 flex gap-1 border-b border-border">
      {tabs.map((t) => {
        const on = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onPick(t.value)}
            className={
              "-mb-px border-b-2 px-3 py-1.5 text-[13px] transition-colors " +
              (on
                ? "border-accent font-medium text-fg"
                : "border-transparent text-fg-muted hover:border-border-strong hover:text-fg")
            }
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Раскадровка. Порядок кадров меняют перетаскиванием за НОМЕР, а не за всю
 * строку: строка редактируемая, и выделение текста мышью не должно
 * превращаться в перетаскивание. Поэтому `draggable` включается, только пока
 * кнопка мыши нажата на номере.
 */
function Storyboard({
  pid,
  data,
  seedAt,
  preview,
  onPreview,
  video,
}: {
  pid: number;
  data: ProjectView;
  seedAt: number;
  preview: PreviewTarget | null;
  onPreview: (target: PreviewTarget | null) => void;
  /** Видео-проект: карточкам кадров — поля клипа из хронологии. */
  video: VideoView | null;
}) {
  const client = useQueryClient();
  const timeline = useTimeline(pid, video, seedAt);
  const clipsOf = useMemo(() => new Map(timeline.map((t) => [t.shot_id, t])), [timeline]);
  const serverOrder = useMemo(() => data.shots.map((s) => s.id), [data.shots]);
  const serverKey = serverOrder.join(",");
  const byId = useMemo(() => new Map(data.shots.map((s) => [s.id, s])), [data.shots]);

  const [order, setOrder] = useState<number[]>(serverOrder);
  const [grab, setGrab] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const rows = useRef(new Map<number, HTMLDivElement>());

  const [uploadOpen, setUploadOpen] = useState<Set<number>>(new Set());
  const [genOpen, setGenOpen] = useState<Set<number>>(new Set());
  const [armed, setArmed] = useState<number | null>(null);
  const [title, setTitle] = useState("");

  // Сервер — правда о порядке; пока тащат, правда — у руки.
  useEffect(() => {
    if (dragging === null) setOrder(serverOrder);
  }, [serverKey]);

  useEffect(() => {
    const release = () => setGrab(null);
    document.addEventListener("mouseup", release);
    return () => document.removeEventListener("mouseup", release);
  }, []);

  const reorder = useMutation({
    mutationFn: (ids: number[]) => postJson<{ moved: number }>(`/api/projects/${pid}/shots/order`, { order: ids }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.project(pid) });
      // Хронология видео-проекта читает порядок и из опроса — пусть перечитается и он.
      if (video) void client.invalidateQueries({ queryKey: videoKeys.poll(pid) });
    },
    onError: () => setOrder(serverOrder),
  });

  const addShot = useMutation({
    mutationFn: (value: string) =>
      postJson<{ id: number; shot_order: number; shot_title: string }>(`/api/projects/${pid}/shots`, { title: value }),
    onSuccess: async () => {
      setTitle("");
      await client.invalidateQueries({ queryKey: qk.project(pid) });
    },
  });

  const toggle = (set: (fn: (prev: Set<number>) => Set<number>) => void, id: number) =>
    set((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onDragOver = (y: number) => {
    if (dragging === null) return;
    const rest = order.filter((id) => id !== dragging);
    // Куда встанет кадр: перед первой строкой, чья середина ниже курсора.
    const before = rest.findIndex((id) => {
      const box = rows.current.get(id)?.getBoundingClientRect();
      return box !== undefined && y < box.top + box.height / 2;
    });
    const next = [...rest];
    next.splice(before < 0 ? rest.length : before, 0, dragging);
    if (next.join(",") !== order.join(",")) setOrder(next);
  };

  const onDragEnd = () => {
    setDragging(null);
    setGrab(null);
    if (order.join(",") !== serverKey) reorder.mutate(order);
  };

  return (
    <Panel title={`Раскадровка — ${data.shots.length} кадров`} annot="раскадровка">
      <Hint>
        Заголовок и промпт правятся здесь же — щёлкните по тексту. Порядок меняется перетаскиванием за номер кадра.
        Фотографии кадра: «+», перетаскивание или Ctrl-V.
      </Hint>
      <ErrorLine text={reorder.isError ? `Порядок не сохранился: ${errorText(reorder.error)}` : ""} className="mt-2" />

      <div
        className="mt-3 grid grid-cols-[minmax(0,1fr)] gap-3"
        onDragOver={(e) => {
          if (dragging === null) return;
          e.preventDefault();
          onDragOver(e.clientY);
        }}
      >
        {order.length === 0 ? <p className="text-[12.5px] text-fg-faint italic">Кадров пока нет.</p> : null}
        {order.map((id, n) => {
          const shot = byId.get(id);
          if (!shot) return null;
          return (
            <div
              key={id}
              data-annot={`кадр ${id}`}
              ref={(el) => {
                if (el) rows.current.set(id, el);
                return () => {
                  rows.current.delete(id);
                };
              }}
              draggable={grab === id}
              onDragStart={(e) => {
                if (grab !== id) return;
                setDragging(id);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", String(id));
              }}
              onDragEnd={onDragEnd}
              className={
                "rounded-[var(--radius-card)] border bg-bg-subtle p-3 transition-opacity " +
                (dragging === id ? "border-accent opacity-50" : "border-border")
              }
            >
              <ShotCard
                pid={pid}
                shot={shot}
                number={n + 1}
                slots={data.slots}
                options={data}
                seedAt={seedAt}
                pending={data.pending}
                uploadOpen={uploadOpen.has(id)}
                genOpen={genOpen.has(id)}
                armed={armed === id}
                onToggleUpload={() => {
                  const opening = !uploadOpen.has(id);
                  toggle(setUploadOpen, id);
                  setArmed(opening ? id : armed === id ? null : armed);
                }}
                onToggleGen={() => toggle(setGenOpen, id)}
                onArm={() => setArmed(id)}
                onHandleDown={() => setGrab(id)}
                openVariant={preview?.sid === id ? preview.vid : null}
                onOpenVariant={(vid) => onPreview({ sid: id, vid, where: `${n + 1}. ${shot.shot_title}` })}
                video={video ? clipsOf.get(id) : undefined}
              />
            </div>
          );
        })}
      </div>

      <form
        className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!addShot.isPending) addShot.mutate(title.trim());
        }}
      >
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="заголовок кадра, например «Хук: взгляд в камеру»"
          className={`${inputClass} min-w-[280px] flex-1`}
        />
        <Btn type="submit" tone="primary" disabled={addShot.isPending}>
          <Plus size={13} />
          Добавить кадр
        </Btn>
      </form>
      <ErrorLine text={addShot.isError ? errorText(addShot.error) : ""} className="mt-2" />
    </Panel>
  );
}
